'use client';

import { useEffect } from 'react';
import Link from 'next/link';

// Route-level error boundary. Without one, any exception thrown while
// rendering a page (a malformed card from the backend, an unexpected null)
// replaced the entire screen with Next's bare "Application error" page —
// no header, no nav, nothing for the user to do. This keeps the root layout
// (header, state nav, footer still render around it) and offers a retry,
// which re-runs the server render for this route.
export default function RouteError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Surfaces in the browser console and in any client error reporting;
    // the server-side stack is already in the pod logs under the digest.
    console.error('Route render error', error.digest ?? '', error);
  }, [error]);

  return (
    <div className="flex flex-col items-center justify-center gap-4 px-4 py-16 text-center">
      <h1 className="text-xl font-semibold text-gray-900">Something went wrong</h1>
      <p className="max-w-md text-sm text-gray-600">
        We couldn&apos;t load this page. Please try again, or head back to the
        home page for the latest news.
      </p>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={reset}
          className="rounded-full bg-red-500 px-4 py-1.5 text-sm font-semibold text-white shadow-md hover:bg-red-600"
        >
          Try again
        </button>
        <Link
          href="/"
          prefetch={false}
          className="rounded-full border-2 border-red-500 px-4 py-1.5 text-sm font-semibold text-red-500 hover:bg-red-50"
        >
          Go to home
        </Link>
      </div>
    </div>
  );
}
